import { useEffect, useState } from 'react';
import { Address } from 'react-daum-postcode';
import { useForm } from 'react-hook-form';

import { zodResolver } from '@hookform/resolvers/zod';

import { z } from 'zod';

import { usePostSendEmailVerificationApi, usePostVerifyCodeApi } from '@/api/auth';
import { useGetMemberInfoApi, usePostModifyMemberInfoApi } from '@/api/mypage';
import Field from '@/components/common/Field/Field';
import InputAddress from '@/components/common/Input/InputAddress/InputAddress';
import InputSelect from '@/components/common/Input/InputSelect/InputSelect';
import InputText from '@/components/common/Input/InputText/InputText';
import { daysOptions, emailOptions, monthOptions, yearOptions } from '@/constants/option';
import { useModalStore } from '@/store/modalStore';
import { useToastStore } from '@/store/toastStore';
import { formatCell } from '@/utils/formatting';

import * as S from './style';

interface RegisterField {
	name: string;
	enName: string;
	birth: { year: string; month: string; day: string };
	gender: string;
	address: { zipcode: string; address: string; detailAddress: string };
	cell: string;
	email: { front: string; back: string; code: string; verificationCode: string };
}
const BasicInfo = () => {
	const [isOpen, setIsOpen] = useState(false);
	const [isEmailVerified, setIsEmailVerified] = useState(false);
	const [isSendEmail, setIsSendEmail] = useState(false);
	const { showModal } = useModalStore();
	const { openToast } = useToastStore();

	const userRegisterSchema = z.object({
		name: z.string().min(1, { message: '이름을 입력해 주세요.' }),
		enName: z.string().min(1, { message: '영문이름을 입력해 주세요.' }),
		birth: z.object({
			year: z.string().min(1, { message: '생년월일을 선택해 주세요.' }),
			month: z.string().min(1, { message: '생년월일을 선택해 주세요.' }),
			day: z.string().min(1, { message: '생년월일을 선택해 주세요.' }),
		}),
		email: z.object({
			front: z.string().min(1, { message: '이메일 주소를 입력해 주세요.' }),
			back: z.string().min(1, { message: '이메일 주소를 입력해 주세요.' }),
			code: z.string(),
			verificationCode: z.string(),
		}),
		gender: z.string().min(1, { message: '성별을 선택해 주세요.' }),
		address: z.object({
			zipcode: z.string().min(1, { message: '우편번호를 입력해 주세요.' }),
			address: z.string().min(1, { message: '주소를 입력해 주세요.' }),
			detailAddress: z.string().min(1, { message: '상세주소를 입력해 주세요.' }),
		}),
		cell: z.string().min(1, { message: '휴대폰번호를 입력해 주세요.' }),
	});
	const {
		register,
		handleSubmit,
		watch,
		setValue,
		formState: { errors },
	} = useForm<RegisterField>({
		resolver: zodResolver(userRegisterSchema),
		mode: 'onChange',
		defaultValues: {
			name: '',
			enName: '',
			birth: { year: '', month: '', day: '' },
			cell: '',
			email: { front: '', back: '', code: '직접입력', verificationCode: '' },
			gender: '남',
			address: { zipcode: '', address: '', detailAddress: '' },
		},
	});
	const { onChange, ...rest } = register('email.verificationCode');
	const { mutateAsync: VerifyCode } = usePostVerifyCodeApi();
	const { mutateAsync: SendEmailVerification } = usePostSendEmailVerificationApi();
	const { data: memberInfo } = useGetMemberInfoApi();
	const { mutateAsync: modifyMemberInfo } = usePostModifyMemberInfoApi();

	const handleBirthYear = (label: string) => {
		setValue('birth.year', label, { shouldValidate: true });
	};
	const handleBirthMonth = (label: string) => {
		setValue('birth.month', label, { shouldValidate: true });
	};
	const handleBirthDay = (label: string) => {
		setValue('birth.day', label, { shouldValidate: true });
	};
	const handleAddress = (val: Address) => {
		setValue(
			'address',
			{
				zipcode: val.zonecode,
				address: val.address,
				detailAddress: '',
			},
			{ shouldValidate: true }
		);
	};
	const handleSendEmailVerification = async () => {
		const res = await SendEmailVerification({ email: `${watch('email.front')}@${watch('email.back')}` });
		if (res.data.success) {
			showModal('입력하신 이메일로 인증번호가 발송되었습니다.\n이메일을 확인하신 후 인증번호를 입력해 주세요.');
			setIsSendEmail(true);
		}
	};
	const handleVerifyCode = async () => {
		const email = `${watch('email.front')}@${watch('email.back')}`;
		const code = watch('email.verificationCode');
		const res = await VerifyCode({ email, code });
		if (res.data.success) {
			showModal('인증이 완료되었습니다.');
			setIsEmailVerified(true);
		} else {
			showModal('인증번호가 일치하지 않습니다.');
			setIsEmailVerified(false);
		}
	};
	const onSubmit = async (data: RegisterField) => {
		const res = await modifyMemberInfo({
			member_name: data.name,
			member_en_name: data.enName,
			member_birth_year: data.birth.year,
			member_birth_month: data.birth.month,
			member_birth_date: data.birth.day,
			member_gender: data.gender,
			member_zipcode: data.address.zipcode,
			member_address: data.address.address,
			member_address_detail: data.address.detailAddress,
			member_cell: data.cell.replace(/-/g, ''),
			member_email: isEmailVerified ? `${data.email.front}@${data.email.back}` : (memberInfo?.member_email ?? ''),
		});

		if (res.data.success) {
			openToast({
				subContent: '대한화장품학회',
				content: '정보가 변경되었습니다.',
			});
		}
	};
	const convertValue = (value: string) => {
		if (value === 'm') return '남';
		if (value === 'f') return '여';
		return value;
	};

	useEffect(() => {
		if (memberInfo) {
			setValue('name', memberInfo.member_name);
			setValue('enName', memberInfo.member_en_name);
			setValue('birth.year', memberInfo.member_birth_year);
			setValue('birth.month', memberInfo.member_birth_month);
			setValue('birth.day', memberInfo.member_birth_date);
			setValue('gender', convertValue(memberInfo.member_gender));
			setValue('address.zipcode', memberInfo.member_zipcode);
			setValue('address.address', memberInfo.member_address);
			setValue('address.detailAddress', memberInfo.member_address_detail);
			setValue('cell', memberInfo.member_cell.replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3'));
			setValue('email.front', memberInfo.member_email.split('@')[0]);
			setValue('email.back', memberInfo.member_email.split('@')[1]);
			setValue('email.code', memberInfo.member_email.split('@')[1]);
		}
	}, [memberInfo]);

	return (
		<S.FormContainer>
			<S.FieldBox>
				<S.Title>기본정보</S.Title>
				<Field title="회원구분">
					<InputText value={memberInfo?.member_type ?? '일반회원'} readonly />
				</Field>
				<Field title="이름" error={errors.name?.message ?? ''}>
					<InputText isError={!!errors.name?.message} placeholder="예) 홍길동" {...register('name')} />
				</Field>
				<Field title="영문이름" error={errors.enName?.message ?? ''}>
					<InputText isError={!!errors.enName?.message} placeholder="예) Hong Gildong" {...register('enName')} />
				</Field>
				<Field title="생년월일" error={errors.birth?.year?.message ?? errors.birth?.day?.message ?? errors.birth?.month?.message ?? ''}>
					<S.FlexRow>
						<InputSelect options={yearOptions} selectedOption={watch('birth.year')} setSelectedOption={handleBirthYear} defaultValue="년도" />
						<InputSelect options={monthOptions} selectedOption={watch('birth.month')} setSelectedOption={handleBirthMonth} defaultValue="월" />
						<InputSelect options={daysOptions} selectedOption={watch('birth.day')} setSelectedOption={handleBirthDay} defaultValue="일" />
					</S.FlexRow>
				</Field>
				<Field title="성별" error={errors.gender?.message ?? ''}>
					<S.GenderBox>
						<S.GenderRadio>
							<label>
								<input type="radio" value="남" {...register('gender')} />남
							</label>
						</S.GenderRadio>
						<S.GenderRadio>
							<label>
								<input type="radio" value="여" {...register('gender')} />여
							</label>
						</S.GenderRadio>
					</S.GenderBox>
				</Field>
				<Field title="아이디">
					<InputText value={memberInfo?.member_id} readonly />
				</Field>
				<Field title="*주소" error={errors.address?.address?.message ?? errors.address?.detailAddress?.message ?? ''}>
					<S.AddressContentBox>
						<S.AddressBox>
							<InputText readonly {...register('address.zipcode')} />
							<S.FindButton type="button" onClick={() => setIsOpen(true)}>
								우편번호 찾기
							</S.FindButton>
						</S.AddressBox>
						<S.AddressBox>
							<InputText readonly {...register('address.address')} />
						</S.AddressBox>
						<S.AddressBox>
							<InputText
								{...register('address.detailAddress')}
								placeholder="상세주소 입력"
								isError={!!errors.address?.address?.message || !!errors.address?.detailAddress?.message}
							/>
						</S.AddressBox>
					</S.AddressContentBox>
				</Field>
				<Field title="휴대폰 번호" error={errors.cell?.message ?? ''}>
					<InputText
						{...Object.assign({}, register('cell'), {
							onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
								const value = formatCell(e);
								setValue('cell', value, { shouldValidate: true });
							},
						})}
						placeholder="‘-’없이 숫자만 입력"
						isError={!!errors.cell?.message}
					/>
				</Field>
				<Field title="이메일 인증" gap="0.8rem">
					<Field error={errors.email?.back?.message ?? errors.email?.front?.message ?? errors.email?.code?.message ?? ''}>
						<S.FlexRow>
							<InputText
								value={watch('email.front')}
								input={(e: React.ChangeEvent<HTMLInputElement>) => {
									setValue('email.front', e.target.value, { shouldValidate: true });
									setIsEmailVerified(false);
								}}
								isError={!!errors.email?.front?.message}
							/>
							@
							<InputText
								input={(e: React.ChangeEvent<HTMLInputElement>) => {
									setValue('email.back', e.target.value, { shouldValidate: true });
									setIsEmailVerified(false);
								}}
								value={watch('email.code') !== '' && watch('email.code') !== '직접입력' ? watch('email.code') : watch('email.back')}
								readonly={watch('email.code') !== '' && watch('email.code') !== '직접입력'}
							/>
							<InputSelect
								options={emailOptions}
								selectedOption={watch('email.code')}
								setSelectedOption={(val) => {
									setValue('email.code', val, { shouldValidate: true });
									if (val !== '직접입력') setValue('email.back', val, { shouldValidate: true });
								}}
								defaultValue="직접입력"
							/>
							<S.FindButton
								type="button"
								onClick={handleSendEmailVerification}
								disabled={watch('email.front') === '' || watch('email.back') === '' || watch('email.code') === ''}
							>
								{'재전송'}
							</S.FindButton>
						</S.FlexRow>
					</Field>
					<S.FlexRow>
						<InputText
							{...rest}
							input={(e) => {
								onChange(e); // register에서 제공한 onChange 호출
								setIsEmailVerified(false);
								console.log(e.target.value, e.target.value === '', isEmailVerified);
							}}
							placeholder="인증번호 입력"
						/>
						<S.FindButton type="button" onClick={handleVerifyCode} disabled={watch('email.verificationCode') === '' || isEmailVerified}>
							인증
						</S.FindButton>
					</S.FlexRow>
					<S.Danger>※ 이메일 인증의 경우 받는 메일서버에 따라 도착이 지연되거나 스팸으로 분류될 수 있습니다.</S.Danger>
				</Field>
			</S.FieldBox>
			<S.ButtonBox>
				<S.NextButton type="submit" onClick={handleSubmit(onSubmit)} disabled={isSendEmail && !isEmailVerified}>
					변경완료
				</S.NextButton>
			</S.ButtonBox>

			{isOpen && <InputAddress setIsOpen={setIsOpen} setAddress={handleAddress} />}
		</S.FormContainer>
	);
};

export default BasicInfo;
